'use client';

import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useAccount } from './account-context';
import { getGiftCardLimitByKeys } from '@lib/data';
import {
    LOGGED_IN_USER_LIMIT_CONFIG_KEYS,
    LOGGED_OUT_USER_LIMIT_CONFIG_KEYS
} from '@lib/constants/max-limit-constant';

interface GiftCardsLimitContext {
    maxSenderAmount: number | undefined;
    maxSenderGiftCards: number | undefined;
    maxRecipientAmount: number | undefined;
    maxRecipientGiftCards: number | undefined;
}

export const GiftCardsLimitContext = createContext<GiftCardsLimitContext | null>(null);

export const GiftCardsLimitProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const { customer, retrievingCustomer } = useAccount()
    const [maxSenderAmount, setMaxSenderAmount] = useState<number | undefined>()
    const [maxSenderGiftCards, setMaxSenderGiftCards] = useState<number | undefined>()
    const [maxRecipientAmount, setMaxRecipientAmount] = useState<number | undefined>()
    const [maxRecipientGiftCards, setMaxRecipientGiftCards] = useState<number | undefined>(0)
    const [limitConfigMap, setLimitConfigMap] = useState<Map<string, number> | undefined>()
    const [limitConfigKeys, setLimitConfigKeys] = useState<string[]>([]);

    useEffect(() => {
        if (retrievingCustomer)
            return;
        customer ? setLimitConfigKeys(LOGGED_IN_USER_LIMIT_CONFIG_KEYS) : setLimitConfigKeys(LOGGED_OUT_USER_LIMIT_CONFIG_KEYS)
    }, [retrievingCustomer, customer])

    useEffect(() => {
        if (limitConfigKeys.length > 0)
            fetchLimitKeys(limitConfigKeys)
    }, [limitConfigKeys])

    const fetchLimitKeys = async (keys: string[]) => {
        try {
            const _limitConfigMap = await getGiftCardLimitByKeys(keys)
            _limitConfigMap && setLimitConfigMap(_limitConfigMap)
        } catch (error) {
            console.error('Cannot fetch limit keys', error)
        }
    }

    useEffect(() => {
        if (limitConfigMap) {
            setMaxSenderGiftCards(limitConfigMap.get(limitConfigKeys[0]))
            setMaxSenderAmount(limitConfigMap.get(limitConfigKeys[1]))
            setMaxRecipientGiftCards(limitConfigMap.get(limitConfigKeys[2]))
            setMaxRecipientAmount(limitConfigMap.get(limitConfigKeys[3]))
        }
    }, [limitConfigMap])

    const contextValue: GiftCardsLimitContext = useMemo(
        () => ({
            maxSenderAmount,
            maxSenderGiftCards,
            maxRecipientAmount,
            maxRecipientGiftCards,
        }),
        [
            maxSenderAmount,
            maxSenderGiftCards,
            maxRecipientAmount,
            maxRecipientGiftCards
        ]
    );

    return (
        <GiftCardsLimitContext.Provider value={contextValue}>
            {children}
        </GiftCardsLimitContext.Provider>
    );
};

export const useGiftCardsLimitContext = () => {
    const context = useContext(GiftCardsLimitContext);

    if (context === null) {
        throw new Error(
            'useGiftCardsLimitContext must be used within a GiftCardsLimitProvider'
        );
    }

    return context;
};
